import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import {
  SCHEDULES_QUERY,
  SCHEDULE_GROUPS_QUERY,
  SCHEDULE_DETAIL_QUERY,
  SCHEDULE_ITEMS_QUERY,
} from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
// Direct module paths, NOT barrels — see the note in `useAssetsPrefetch`.
import { resolveItemPageSize, resolveFolderPageSize } from '@/components/shared/FolderItemList/dev';
import { useScheduleSortStore } from '@/features/schedules/stores/schedule-sort-store';
// Leaf module on purpose: `features/schedules/constants` imports `rrule`, and this hook is mounted
// from AppShell, so importing the window from there would drag ~50 kB into the main bundle.
import {
  SCHEDULE_ITEMS_FETCH_LIMIT,
  getScheduleItemsWindow,
} from '@/features/schedules/utils/schedule-items-window';

/**
 * `SCHEDULE_PAGE_SIZE` from `features/schedules/constants` — duplicated rather than imported.
 * That module pulls in `rrule` and the whole schedule constant set, and this hook is mounted from
 * AppShell, so importing it would drag all of that into the MAIN bundle to obtain one integer.
 * `prefetch-cache-key-parity.test.ts` asserts this stays equal to the real constant.
 */
const SCHEDULE_PAGE_SIZE = 20;

/**
 * Idle-time prefetch for the Schedules list. See `useAssetsPrefetch` for the full rationale.
 *
 * #1229 specified Schedules as chunk-only, on the grounds that "date-range params make data
 * prefetch unreliable". That does not apply to the LIST query: `schedulesVars`
 * (`useScheduleList.ts:298`) is `{ first, teamId, query, sort }` — structurally identical to
 * Assets and Playlists, with no date range. The date range belongs to the calendar DETAIL panel,
 * which loads a user-selected schedule via `useScheduleDetail` and is correctly not prefetchable.
 * `SchedulesPage` renders the sidebar list unconditionally, so this query always fires on a cold
 * visit. Prefetching it was approved on 2026-09-14 as a deliberate deviation from the ticket.
 *
 * Also warms the DETAIL of whichever schedule the page will auto-select. `SchedulesPage` selects
 * `schedules[0]` on arrival (`:239`, when there is no deep-link id) and the selection store is
 * memory-only, so on a hard reload that detail query always fires. The id is not knowable up
 * front, so the detail prefetch chains off the list result.
 *
 * Note the page size is **20**, not the 50 used by Assets/Playlists.
 *
 * Sort comes from a zustand `persist` store (the only true `persist` user of the three); with the
 * default localStorage adapter rehydration is synchronous at store creation, so there is no race.
 */
export function useSchedulesPrefetch(): void {
  const client = useApolloClient();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const sortField = useScheduleSortStore((s) => s.sortField);
  const sortDirection = useScheduleSortStore((s) => s.sortDirection);

  useEffect(() => {
    if (!activeTeamId) return;

    const run = () => {
      const sort = { [sortField]: sortDirection };

      client
        .query({
          query: SCHEDULES_QUERY,
          variables: {
            first: resolveItemPageSize(SCHEDULE_PAGE_SIZE),
            teamId: activeTeamId,
            // Root: `isValidPath` is false, so `path` is null and `teamId` is not set on the
            // query (useScheduleList.ts:129-133).
            query: { path: null },
            sort,
            // NO extendParams key at defaults — see `usePlaylistsPrefetch`.
          },
          fetchPolicy: 'cache-first',
        })
        .then((res) => {
          const firstId = (
            res?.data as { schedules?: { page?: { edges?: Array<{ node?: { _id?: string } }> } } }
          )?.schedules?.page?.edges?.[0]?.node?._id;
          if (!firstId) return;

          // `useScheduleDetail` keys on `_id` (NOT `id` — Playlists uses `id`).
          const detail = client
            .query({
              query: SCHEDULE_DETAIL_QUERY,
              variables: { _id: firstId, teamId: activeTeamId },
              fetchPolicy: 'cache-first',
            })
            .catch(() => {});

          // The calendar's events — the heaviest query the page issues (`first: 5000`) and the one
          // the panel actually renders, so leaving it cold meant the calendar still round-tripped
          // even with the detail warm. The window comes from the page's own
          // `getScheduleItemsWindow`, day-anchored precisely so every consumer shares one cache
          // entry; ms-precision dates would give each caller a different key.
          const { startDate, endDate } = getScheduleItemsWindow();
          const items = client
            .query({
              query: SCHEDULE_ITEMS_QUERY,
              variables: {
                query: { scheduleId: firstId, startDate, endDate },
                teamId: activeTeamId,
                first: SCHEDULE_ITEMS_FETCH_LIMIT,
              },
              fetchPolicy: 'cache-first',
            })
            .catch(() => {});

          return Promise.all([detail, items]).then(() => undefined);
        })
        .catch(() => {
          // Best-effort — don't surface errors to the user.
        });

      client
        .query({
          query: SCHEDULE_GROUPS_QUERY,
          variables: {
            first: resolveFolderPageSize(),
            teamId: activeTeamId,
            query: { type: 'SCHEDULE', path: null },
            sort,
          },
          fetchPolicy: 'cache-first',
        })
        .catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run);
    } else {
      setTimeout(run, 2000);
    }
  }, [client, activeTeamId, sortField, sortDirection]);
}
